import * as React from "react";
import { ArrowLeft, LocationArrow } from "../../components/Icons";
import { Wrapper, Status } from "@googlemaps/react-wrapper";
import { isLatLngLiteral } from "@googlemaps/typescript-guards";
import { createCustomEqual } from "fast-equals";
import { useIsMobile } from "../../hooks/useIsMobile";

interface IProps {
  name: string;
  direction?: string;
  distance?: number;
  address: string;
  phone: string;
  hours: string;
  mapOpen: boolean;
  setMapOpen: Function; // (boolean) => void
  lat: any;
  lng: any;
}

interface MapProps extends google.maps.MapOptions {
  style: { [key: string]: string };
  onClick?: (e: google.maps.MapMouseEvent) => void;
  onIdle?: (map: google.maps.Map) => void;
}

const StoreMap: React.VFC<IProps> = ({
  name,
  address,
  direction,
  phone,
  hours,
  distance,
  setMapOpen,
  lat,
  lng
}) => {
  const { isMobile } = useIsMobile();

  const [zoom, setZoom] = React.useState(5); // initial zoom
  const [center, setCenter] = React.useState<google.maps.LatLngLiteral>({
    lat: -25.274398,
    lng: 133.775136
  }); // centre of Australia
  const [position, setPosition] = React.useState({
    lat: -25.274398,
    lng: 133.775136
  });

  const handleClose = () => {
    setMapOpen(false);
  };

  const handleOpen = () => {
    // const lat = parseFloat(/\@(-?[\d\.]*)/.exec(direction)[1])
    // const lng = parseFloat(/\@[-?\d\.]*\,([-?\d\.]*)/.exec(direction)[1])

    setCenter({ lat, lng });
    setZoom(14);
    setPosition({ lat, lng });
  };

  React.useEffect(() => {
    handleOpen();
  }, []);


  function serialize(obj) {
    if (obj.type === "root") {
      return obj.children.map(child => serialize(child)).join("\n");
    }
    if (obj.type === "paragraph") {
      const text = obj.children.map(child => serialize(child)).join(" ");
      return "<p>" + text + "</p>";
    }
    if (obj.type === "text") {
      return obj.value;
    }
    return "";
  }


  if (isMobile)
    return (
      <div className="StoreMap__wrapper fixed left-0 right-0 bg-white bottom-0 flex">
        <div className="Content__wrapper px-12 py-4 min-w-3/8 max-w-lg">
          <button
            onClick={handleClose}
            className="flex gap-1 underline text-sm mb-5 items-center"
          >
            <ArrowLeft width={10} /> Back to store results
          </button>
          <h3 className="text-4xl uppercase tracking-widest font-light mb-1">
            {name.split("- ")[1]}
          </h3>

          {distance > 0 && (
            <p className="text-gray-700 text-lg font-bold">
              {distance}kms from search
            </p>
          )}
        </div>

        <div className="g_map--wrapper flex-1">
          <Wrapper
            apiKey={"AIzaSyAQPVUx-B64ZnF83_CD43APcKCUpe04X7Q"}
            render={render}
          >
            <Map
              center={center}
              zoom={zoom}
              style={{ flexGrow: "1", height: "100%" }}
              disableDefaultUI={true}
            >
              <Marker position={position} />
            </Map>
          </Wrapper>
        </div>

        <div className="Content__wrapper px-12 py-4 min-w-3/8 max-w-lg">
          <h3 className="text-4xl uppercase tracking-widest font-light mb-1">
            {name.split("- ")[1]}
          </h3>
          <address
            className="mb-4 mt-3 text-base font-light not-italic"
            dangerouslySetInnerHTML={{ __html: serialize(address) }}
          ></address>
          <h4 className="text-lg font-bold underline capitalize mb-4">
            <a
              className="flex gap-2"
              href={direction}
              target="_blank"
              rel="noopener"
            >
              <LocationArrow width={16} /> Directions
            </a>
          </h4>
          <h5 className="font-bold text-lg text-gray-900">
            Store opening hours
          </h5>
          <div
            className="mt-1 mb-4"
            dangerouslySetInnerHTML={{ __html: serialize(hours) }}
          ></div>
          <h5 className="font-bold text-lg text-gray-900">
            Store phone number
          </h5>
          <a
            href={`tel:${phone.replace(/\s+/g, "")}`}
            className="text-xl tracking-wider font-light hover:text-gray-700 underline hover:underline"
          >
            {phone}
          </a>
        </div>
      </div>
    );

  return (
    <div className="StoreMap__wrapper fixed left-0 right-0 bg-white bottom-0 flex">
      <div className="Content__wrapper px-12 py-4 min-w-3/8 max-w-lg">
        <button
          onClick={handleClose}
          className="flex gap-1 underline text-sm mb-5 items-center"
        >
          <ArrowLeft width={10} /> Back to store results
        </button>
        <h3 className="text-4xl uppercase tracking-widest font-light mb-1">
          {name.split("- ")[1]}
        </h3>

        {distance > 0 && (
          <p className="text-gray-700 text-lg font-bold">
            {distance}kms from search
          </p>
        )}

        <address
          className="mb-4 mt-3 text-base font-light not-italic"
          dangerouslySetInnerHTML={{ __html: serialize(address) }}
        ></address>
        <h4 className="text-lg font-bold underline capitalize mb-4">
          <a
            className="flex gap-2"
            href={direction}
            target="_blank"
            rel="noopener"
          >
            <LocationArrow width={16} /> Directions
          </a>
        </h4>
        <h5 className="font-bold text-lg text-gray-900">Store opening hours</h5>
        <div
          className="mt-1 mb-4"
          dangerouslySetInnerHTML={{ __html: serialize(hours) }}
        ></div>
        <h5 className="font-bold text-lg text-gray-900">Store phone number</h5>
        <a
          href={`tel:${phone.replace(/\s+/g, "")}`}
          className="text-xl tracking-wider font-light hover:text-gray-700  underline"
        >
          {phone}
        </a>
      </div>
      <div className="g_map--wrapper flex-1">
        <Wrapper
          apiKey={"AIzaSyAQPVUx-B64ZnF83_CD43APcKCUpe04X7Q"}
          render={render}
        >
          <Map
            center={center}
            zoom={zoom}
            style={{ flexGrow: "1", height: "100%" }}
            disableDefaultUI={true}
            gestureHandling="greedy"
          >
            <Marker position={position} />
          </Map>
        </Wrapper>
      </div>
    </div>
  );
};

const render = (status: Status) => {
  return <h1>{status}</h1>;
};

const Map: React.FC<MapProps> = ({
  onClick,
  onIdle,
  children,
  style,
  ...options
}) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const [map, setMap] = React.useState<google.maps.Map>();

  React.useEffect(() => {
    if (ref.current && !map) {
      setMap(new window.google.maps.Map(ref.current, {}));
    }
  }, [ref, map]);

  // because React does not do deep comparisons, a custom hook is used
  // see discussion in https://github.com/googlemaps/js-samples/issues/946
  useDeepCompareEffectForMaps(() => {
    if (map) {
      map.setOptions(options);
    }
  }, [map, options]);

  React.useEffect(() => {
    if (map) {
      ["click", "idle"].forEach(eventName =>
        google.maps.event.clearListeners(map, eventName)
      );

      if (onClick) {
        map.addListener("click", onClick);
      }

      if (onIdle) {
        map.addListener("idle", () => onIdle(map));
      }
    }
  }, [map, onClick, onIdle]);

  return (
    <>
      <div ref={ref} style={style} />
      {React.Children.map(children, child => {
        if (React.isValidElement(child)) {
          // set the map prop on the child component
          return React.cloneElement(child, { map });
        }
      })}
    </>
  );
};

const Marker: React.FC<google.maps.MarkerOptions> = options => {
  const [marker, setMarker] = React.useState<google.maps.Marker>();

  React.useEffect(() => {
    if (!marker) {
      setMarker(new google.maps.Marker());
    }

    // remove marker from map on unmount
    return () => {
      if (marker) {
        marker.setMap(null);
      }
    };
  }, [marker]);

  React.useEffect(() => {
    if (marker) {
      marker.setOptions(options);
    }
  }, [marker, options]);

  return null;
};

const deepCompareEqualsForMaps = createCustomEqual(
  deepEqual => (a: any, b: any) => {
    if (
      isLatLngLiteral(a) ||
      a instanceof google.maps.LatLng ||
      isLatLngLiteral(b) ||
      b instanceof google.maps.LatLng
    ) {
      return new google.maps.LatLng(a).equals(new google.maps.LatLng(b));
    }

    // TODO extend to other types

    // use fast-equals for other objects
    return deepEqual(a, b);
  }
);

function useDeepCompareMemoize(value: any) {
  const ref = React.useRef();

  if (!deepCompareEqualsForMaps(value, ref.current)) {
    ref.current = value;
  }

  return ref.current;
}

function useDeepCompareEffectForMaps(
  callback: React.EffectCallback,
  dependencies: any[]
) {
  React.useEffect(callback, dependencies.map(useDeepCompareMemoize));
}

export default StoreMap;
